import React from 'react';
import { useTable, usePagination, useRowSelect, Column } from 'react-table';
import { useSticky } from 'react-table-sticky';
import { SetStateAction } from 'react';

import { Button } from 'components/commons';
import { ComponentsLoader } from 'components';
import { TableHeader, ActionWithRow, CustomTableCell, IndeterminateCheckbox, CustomFilterColumn, TableSearch } from "..";

import sortDisable from "assets/images/sortDisable.svg";
import sortASC from "assets/images/sortASC.svg";
import sortDESC from "assets/images/sortDESC.svg";
import trashBin from "assets/images/trashBin.svg";
import pin from "assets/images/pin.svg";
import pinActive from "assets/images/pinActive.svg";
import "./Table.scss";

import Tooltip from "../../../../components/Tooltip/Tooltip";
import {IColumnData, IFilterOptionsWrapper, IRowData} from "../../../../constants/Table/interfaces";
import Paginator from "../../../../components/Tables/DefaultTable/Paginator";

// Table

interface ITableProps {
  columns: IColumnData[],
  data: object[],
  updateProduct: (rowIndex: number, id: string, columnId: string, value: string) => void,
  addColumn: (column: IColumnData) => void,
  deleteColumn: (column: IColumnData) => void,
  deleteRow: (row: IRowData) => void,
  rowDataLoading: boolean,
  productsRequestError: string,
  tableSettings: any,
  setTableSettings: (data: SetStateAction<any>) => void,
  totalProducts: number,
  setRowDataLoading: (data: boolean) => void,
  tableSettingsLoaded: boolean,
  togglePinColumnHandler: (data: Column) => void,
  setColumnData: (data: IColumnData[]) => void,
  setTableSettingsToEqualCheck: React.Dispatch<React.SetStateAction<IFilterOptionsWrapper | undefined>>
}

const Table: React.FC<ITableProps> = React.memo((
  {
    columns,
    data,
    updateProduct,
    addColumn,
    deleteColumn,
    deleteRow,
    rowDataLoading,
    productsRequestError,
    tableSettings,
    setTableSettings,
    totalProducts,
    setRowDataLoading,
    tableSettingsLoaded,
    togglePinColumnHandler,
    setColumnData,
    setTableSettingsToEqualCheck
  }) => {

  const defaultColumn:any = React.useMemo(() => ({
    Filter: CustomFilterColumn,
    Cell: CustomTableCell,
    minWidth: 75,
    width: 75,
  }), []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setPageSize,
    selectedFlatRows,
    state: { pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      updateProduct,
      autoResetFilters: false,
      autoResetSortBy: false
    },
    useSticky,
    usePagination,
    useRowSelect,
    hooks => {
      hooks.visibleColumns.push(columns => [
        {
          id: 'selection',
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div className='flex justify-center content-center'>
              <IndeterminateCheckbox name={''} {...getToggleAllRowsSelectedProps()} />
              {/*<Button clickHandler={removeAllFilters} customClassName='mt-4'>*/}
              {/*  <img src={trashBin} alt="delete" />*/}
              {/*</Button>*/}
            </div>
          ),

          Cell: ({ row }) => (
            <div className="flex justify-center content-center">
              <IndeterminateCheckbox name={''} {...row.getToggleRowSelectedProps()} />
            </div>
          ),

          minWidth: 34,
          width: 34,
          originalWidth: 34,
        },
        ...columns,
      ])
    }
  )

  const removeAllFilters = () => {
    setTableSettings((prev: any) => {
      return {
        ...prev,
        // filter: {
        //   data: {
        //     rules: [],
        //     combinator: prev.filter.data.combinator,
        //     not: prev.filter.data.not,
        //   }
        // },
        order: {}
      }
    })
  }

  React.useEffect(() => {
    if (tableSettingsLoaded) {
      setPageSize(tableSettings.limit)
    }
  }, [tableSettingsLoaded])

  const setTablePageSize = (pageSize: number) => {
    setTableSettings((prev: any) => {
      return {
        ...prev,
        limit: pageSize
      }
    })
  }

  // sort logic
  const setSortColumn = (field: string | undefined, direction: "desc" | "asc" | "", channel: string | undefined, part: string | undefined) => {
    if (direction) {
      setTableSettings((prev: any) => {
        return {
          ...prev,
          order: {
            part: part,
            channel: channel,
            field: field,
            direction: direction
          }
        }
      })
    } else {
      setTableSettings((prev: any) => {
        return {
          ...prev,
          order: {}
        }
      })
    }
  }

  const renderSwitchSort = (order: string, field: string | undefined, channel: string | undefined, part: string | undefined) => {
    switch (order) {
      case "asc": return (
        <Button clickHandler={() => setSortColumn(field, "", channel, part)} customClassName="mr-2 inline-block">
          <img src={sortASC} alt="asc" />
        </Button>
      )

      case "desc": return (
        <Button clickHandler={() => setSortColumn(field, "asc", channel, part)} customClassName="mr-2 inline-block">
          <img src={sortDESC} alt="desc" />
        </Button>
      )

      default: (
        <Button clickHandler={() => setSortColumn(field, "", channel, part)} customClassName="mr-2 inline-block">
          <img src={sortDisable} alt="standart" />
        </Button>
      )
    }
  }

  const renderFloatSwitch = (params: string) => {
    switch (params) {
      case 'right':
        return('rightCol');
        break;
      case 'center':
        return('centerCol');
        break;
      default:
        return('leftCol');
    }
  }

  const renderFloatTooltipSwitch = (params: string) => {
    switch (params) {
      case 'right':
        return('bottom-right');
        break;
      case 'center':
        return('bottom');
        break;
      default:
        return('bottom-left');
    }
  }

  const [selectLimitOptions] = React.useState<Array<number>>([20, 100, 500, 1000]);

  const pageSizeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(parseInt(e.target.value));
    setTablePageSize(parseInt(e.target.value));
  };

  return (
    <>
      <div className="rounded-sm w-full">
        <TableHeader
          setTablePageSize={setTablePageSize}
          tableSettings={tableSettings}
          totalProducts={totalProducts}
          setPageSize={setPageSize}
          selectedRows={selectedFlatRows}
          addColumn={addColumn}
          deleteColumn={deleteColumn}
          columns={columns}
          deleteRow={deleteRow}
          setColumnData={setColumnData}
        />
        <div className={`${rowDataLoading ? "overflow-hidden" : "overflow-x-auto"} relative ${productsRequestError ? "min-h-[2rem]" : "min-h-[2rem]"} max-h-[77rem]`}>
          {
            !rowDataLoading && data.length <= 0 && !productsRequestError &&
            <div className="absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]">Не найдено ни одного продукта</div>
          }
          {rowDataLoading &&
            <div className="absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]"><ComponentsLoader size={100} /></div>
          }
          <table className="react-table sticky" {...getTableProps()}>
            <thead className="header">
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, i) => {
                    return (
                      <th
                          //@ts-ignore
                        className={`${i === 0 ? "sticky z-10 left-0" : "z-2"} ${renderFloatSwitch(column.float)}`}
                        data-sticky-td={column.id === "selection" && true} 
                        // data-sticky-last-left-td={column.id === "selection"}

                        {...column.getHeaderProps({style: { minWidth: column.minWidth, width: column.width, maxWidth: column.maxWidth },})}
                        >
                        {/*{*/}

                        {/*  column.id !== "selection" &&*/}
                        {/*  (*/}
                        {/*      (*/}
                        {/*      //@ts-ignore*/}
                        {/*      tableSettings && Object.keys(tableSettings.order).length && tableSettings.order.field === column.id && tableSettings.order.direction) ?*/}
                        {/*      (*/}
                        {/*        renderSwitchSort(*/}
                        {/*          //@ts-ignore*/}
                        {/*          tableSettings.order.direction, column.id as string, column.channel as string,column.part as string*/}
                        {/*        )*/}
                        {/*      )*/}
                        {/*      :*/}
                        {/*      tableSettings &&*/}
                        {/*      (*/}
                        {/*        <Button clickHandler={() => setSortColumn(*/}
                        {/*          //@ts-ignore*/}
                        {/*          column.id as string, "desc", column.channel as string, column.part as string*/}
                        {/*        )} customClassName="mr-2 inline-block">*/}
                        {/*          <img src={sortDisable} alt="standart" />*/}
                        {/*        </Button>*/}
                        {/*      )*/}
                        {/*  )*/}
                        {/*}*/}
                        {i !== 0
                            ?

                            <Tooltip title={`${typeof (column.render('Header')) === 'object' ? column.render('Footer') : column.render('Header')}`}
                                     direction={
                                        //@ts-ignore
                                       renderFloatTooltipSwitch(column.float)
                                     }>
                              <div className="text-ellipsis overflow-hidden ">
                                <div className={"font-semibold whitespace-pre"}>
                                  <div className={"flex items-center text-c-light-black custom-float"}>
                                    {column.render('Header')}
                                    <Button clickHandler={() => togglePinColumnHandler(column)} customClassName="ml-1 inline-block min-w-[12px]">
                                      {
                                        column.hasOwnProperty("sticky") && column.sticky ? <img width="12px"  src={pinActive} alt="pin" /> : <img width="12px" src={pin} alt="pin active" />
                                      }
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </Tooltip>
                            :
                            <div className="text-ellipsis overflow-hidden flex flex-col justify-start">
                                {column.render('Header')}
                            </div>
                        }

                        {/*<div className="mt-2">{column.id !== "selection" ?*/}
                        {/*  <TableSearch*/}
                        {/*    tableSettingsLoaded={tableSettingsLoaded}*/}
                        {/*    tableSettings={tableSettings}*/}
                        {/*    column={column}*/}
                        {/*    setRowDataLoading={setRowDataLoading}*/}
                        {/*    setTableSettingsToEqualCheck={setTableSettingsToEqualCheck}*/}
                        {/*  />*/}
                        {/*  :*/}
                        {/*  null*/}
                        {/*}</div>*/}
                      </th>
                    )
                  })}
                  {/*<th>*/}
                  {/*  <div className="sticky z-10"></div>*/}
                  {/*</th>*/}
                </tr>
              ))}
            </thead>
            {
              !rowDataLoading && data.length > 0 && !productsRequestError &&
              <tbody {...getTableBodyProps()} className="body">
                {page.map((row, i) => {
                  prepareRow(row)
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => {
                        if (cell.column.id === "selection") {
                          return <td className="sticky left-0" data-sticky-td="true" {...cell.getCellProps()}>{cell.render('Cell')}</td>
                        }
                        //@ts-ignore
                        return <td {...cell.getCellProps({className: renderFloatSwitch(cell.column.float)})}>{cell.render('Cell')}</td>
                      })}
                      {/*<td className="m-0 p-4 border border-slate-200 bg-white sticky right-0 z-10" data-sticky-td="true" data-sticky-first-right-td="true" {...row.getRowProps()}>*/}
                      {/*  <ActionWithRow deleteRow={deleteRow} row={row.original} />*/}
                      {/*</td>*/}
                    </tr>
                  )
                })}
              </tbody>
            }
          </table>
        </div>
        {
          productsRequestError &&
          <div className="font-semibold text-center my-4 text-red-500">При получении продуктов произошла ошибка: {productsRequestError}</div>
        }
      </div>
      <div className="flex flex-col-reverse sm:flex-row items-end sm:items-start sm:justify-between mt-4">

        {tableSettingsLoaded &&
          <Paginator
              tableSettings={tableSettings}
              pageSize={pageSize}
              setTableSettings={setTableSettings}
              totalProducts={totalProducts}
          />
        }
        <div className="flex items-center my-2 md:my-0">
          <div className="flex items-center">
            <div>Показать от 1 до</div>
            <div className="mx-2">
              <select
                  value={tableSettings && tableSettings.limit}
                  onChange={(e) => pageSizeHandler(e)}
                  style={{ border: '1px solid #e2e8f0' }}
                  className="border border-slate-200 py-1">
                {selectLimitOptions.map(item => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                ))}
              </select>
            </div>
            <div>из {totalProducts && totalProducts} результатов</div>
          </div>
        </div>

      </div>
    </>
  )
})

export default Table;